/**
 * Unique local profiles for the 10 planned suburb pages (Excel Dallas tab).
 *
 * This is the content that makes each `/dallas/{suburb}/{product}` page unique:
 * the product sections come from PILLAR_CONTENT (different per product), and this
 * profile supplies genuinely different LOCAL context per suburb (real industries,
 * districts, and business scenarios - no invented facts). Combined, no two of the
 * 80 pages share both their product content and their local content.
 *
 * Facts here are limited to durable, verifiable local detail (districts, corridors,
 * iconic landmarks, dominant industries) - not volatile single-company claims.
 */

export type SuburbScenario = { h: string; p: string };

export type SuburbProfile = {
  slug: string;
  city: string;
  county: string;
  /** Two short paragraphs of unique local-economy context. */
  economy: [string, string];
  /** Dominant local industries (real). */
  industries: string[];
  /** Notable business districts / corridors / landmarks (real). */
  districts: string[];
  /** Local business situations where financing helps (suburb-specific). */
  scenarios: [SuburbScenario, SuburbScenario, SuburbScenario];
  /** One-line proximity / logistics note to the Dallas office. */
  proximityNote: string;
  /** Nearby planned suburbs (for internal links to the same product). */
  nearby: string[];
};

export const SUBURB_PROFILES: Record<string, SuburbProfile> = {
  plano: {
    slug: "plano",
    city: "Plano",
    county: "Collin County",
    economy: [
      "Plano anchors the affluent northern edge of the metro, where master-planned corporate campuses sit beside some of the highest household incomes in Texas. The Legacy West and Granite Park districts turned the city into a magnet for corporate offices, financial-services teams, and the clinics, restaurants, and service firms that grow up around a white-collar workforce.",
      "That mix shapes how Plano businesses borrow. A boutique medical practice off Preston Road, a software vendor in a Legacy office, and a fast-casual group in The Shops at Legacy all carry very different balance sheets, and each needs a lender who reads Collin County revenue and lease structures correctly rather than a one-size form.",
    ],
    industries: [
      "Corporate & professional services",
      "Financial services & fintech",
      "Healthcare & medical practices",
      "Technology & SaaS",
      "Restaurants & upscale retail",
      "Real estate & construction",
    ],
    districts: [
      "Legacy West & The Shops at Legacy",
      "Granite Park",
      "Preston Road corridor",
      "Downtown Plano (DART Red Line)",
    ],
    scenarios: [
      {
        h: "Scaling a Legacy-area service firm",
        p: "Professional and tech firms near Legacy West often need a line of credit to cover payroll between large client invoices rather than a fixed term loan.",
      },
      {
        h: "Opening a second clinic",
        p: "Healthcare groups expanding along Preston Road use SBA and equipment financing to fit out a second location without draining cash reserves.",
      },
      {
        h: "Restaurant build-outs",
        p: "Operators in The Shops at Legacy and Downtown Plano use short-term capital for equipment, remodels, and seasonal staffing.",
      },
    ],
    proximityNote:
      "Most Plano files close remotely; the office is a short drive south on the Dallas North Tollway when an in-person meeting helps.",
    nearby: ["frisco", "richardson", "mckinney"],
  },

  irving: {
    slug: "irving",
    city: "Irving",
    county: "Dallas County",
    economy: [
      "Irving is built around Las Colinas, one of the country's original master-planned business districts, and around its front-door access to DFW International Airport. The result is an economy weighted toward corporate offices, logistics, conventions, and the hospitality that moves through the Urban Center and the Toyota Music Factory.",
      "For a borrower, that airport-and-office mix means cash flow that often runs on corporate payment cycles. An Irving freight broker, a Las Colinas staffing agency, and a hotel-adjacent caterer each wait on net-30 to net-60 receivables, which is why receivables-based and working-capital structures tend to fit better here than rigid term debt.",
    ],
    industries: [
      "Corporate & regional offices",
      "Logistics & freight (DFW Airport)",
      "Hospitality, events & conventions",
      "Professional & staffing services",
      "Telecom & technology",
    ],
    districts: [
      "Las Colinas Urban Center",
      "Mandalay Canal",
      "DFW Airport corridor",
      "Toyota Music Factory",
    ],
    scenarios: [
      {
        h: "Freight & logistics cash flow",
        p: "Brokers and carriers near the airport use invoice factoring and A/R financing to get paid the day a load delivers instead of weeks later.",
      },
      {
        h: "Staffing & corporate services",
        p: "Las Colinas staffing and services firms bridge payroll against slow-paying corporate clients with a revolving line of credit.",
      },
      {
        h: "Hospitality & events",
        p: "Caterers and event vendors around the Music Factory use short-term and same-day capital to staff up for conventions and peak seasons.",
      },
    ],
    proximityNote:
      "Irving sits minutes from the office via SH-114 or the Tollway; nearly every file is handled by phone and secure upload.",
    nearby: ["grand-prairie", "carrollton"],
  },

  garland: {
    slug: "garland",
    city: "Garland",
    county: "Dallas County",
    economy: [
      "Garland is one of the metro's true manufacturing towns. Decades of industrial investment along the I-635 and I-30 corridors left it with a dense base of factories, fabricators, distributors, and the trades that supply them. It's a working economy more than a corporate one.",
      "Manufacturers and distributors here borrow against equipment, inventory, and purchase orders. A Garland metal shop buying a press, a food distributor restocking before a big order, and an HVAC contractor floating materials all need capital that matches an asset or a contract, not just a credit score.",
    ],
    industries: [
      "Manufacturing & fabrication",
      "Distribution & wholesale",
      "Industrial trades (HVAC, electrical)",
      "Automotive & repair",
      "Retail around Firewheel",
    ],
    districts: [
      "I-635 / LBJ industrial corridor",
      "Downtown Garland (DART Blue Line)",
      "Firewheel Town Center",
      "Lake Ray Hubbard commercial",
    ],
    scenarios: [
      {
        h: "Buying production equipment",
        p: "Garland fabricators and machine shops use equipment financing so a new press or CNC pays for itself out of the work it wins.",
      },
      {
        h: "Inventory & purchase-order funding",
        p: "Distributors restocking ahead of a large order use working capital and short-term loans to cover inventory before payment lands.",
      },
      {
        h: "Trades floating materials",
        p: "HVAC, electrical, and contractor crews use a line of credit to buy materials up front and bill the job later.",
      },
    ],
    proximityNote:
      "Garland is a straight shot west on I-635; most owners never need to leave the shop floor to get funded.",
    nearby: ["richardson", "mesquite"],
  },

  frisco: {
    slug: "frisco",
    city: "Frisco",
    county: "Collin County",
    economy: [
      "Frisco grew faster than almost any city in America, and its economy reflects it: sports and entertainment anchors like The Star and the PGA of America headquarters, wrapped in a constant churn of new retail, healthcare, restaurants, and the construction trades building it all.",
      "Growth-stage businesses dominate, and growth eats cash. A Frisco restaurant chasing its second location, a med-spa near Stonebriar, and a framing contractor booked months out all face the same problem: demand is ahead of the bank balance. That's where lines of credit and fast working capital come in.",
    ],
    industries: [
      "Sports, events & entertainment",
      "Construction & real estate",
      "Healthcare, dental & med-spa",
      "Restaurants & franchising",
      "Retail & professional services",
    ],
    districts: [
      "The Star & Frisco Station",
      "PGA of America district",
      "Stonebriar Centre",
      "Preston & Legacy corridors",
    ],
    scenarios: [
      {
        h: "Second-location expansion",
        p: "Frisco restaurant and franchise groups use SBA and short-term loans to open a second unit while the first is still ramping.",
      },
      {
        h: "Construction draw gaps",
        p: "Builders and trades use working capital and A/R financing to cover materials and labor between project draws.",
      },
      {
        h: "Equipping a new clinic",
        p: "Dental, med-spa, and healthcare startups near Stonebriar finance build-out and equipment without tying up reserves.",
      },
    ],
    proximityNote:
      "Frisco is north on the Tollway; we handle the entire file remotely so growth doesn't wait on a meeting.",
    nearby: ["plano", "mckinney"],
  },

  mckinney: {
    slug: "mckinney",
    city: "McKinney",
    county: "Collin County",
    economy: [
      "McKinney pairs a beloved historic square with a fast-growing aerospace and manufacturing base around McKinney National Airport. It's a city of established makers and independent main-street businesses sitting inside one of the fastest-growing counties in the country.",
      "That split shows up in financing. A precision manufacturer near the airport, a wire-and-cable producer, and a boutique on the historic square borrow very differently: one against equipment and contracts, the other against seasonal retail cash flow. Both do better with a lender who knows Collin County.",
    ],
    industries: [
      "Aerospace & defense",
      "Manufacturing (wire, cable, precision)",
      "Healthcare & professional services",
      "Historic-downtown retail & dining",
      "Construction & real estate",
    ],
    districts: [
      "Historic Downtown McKinney Square",
      "McKinney National Airport / aerospace",
      "Craig Ranch",
      "US-75 corridor",
    ],
    scenarios: [
      {
        h: "Aerospace & precision manufacturing",
        p: "Makers near McKinney National Airport use equipment financing and SBA loans to add capacity for defense and industrial contracts.",
      },
      {
        h: "Main-street retail seasonality",
        p: "Shops and restaurants on the historic square use short-term and same-day capital to stock up before festivals and the holidays.",
      },
      {
        h: "Contract-driven working capital",
        p: "Manufacturers awarded a large order use a line of credit to fund materials and labor before the invoice is paid.",
      },
    ],
    proximityNote:
      "McKinney is up US-75 from the office; files move start-to-funding by phone and secure upload.",
    nearby: ["frisco", "plano"],
  },

  "grand-prairie": {
    slug: "grand-prairie",
    city: "Grand Prairie",
    county: "Dallas & Tarrant Counties",
    economy: [
      "Grand Prairie sits squarely between Dallas and Fort Worth, which made it a hub for aerospace manufacturing, distribution, and large-scale entertainment. Lone Star Park and major distribution centers share the map with defense-adjacent plants along the I-20 and SH-161 corridors.",
      "Those are asset- and contract-heavy businesses. A Grand Prairie distributor, an aerospace subcontractor, and a venue-services vendor all borrow against equipment, inventory, or receivables far more often than against a storefront, so the right structure is usually equipment, factoring, or a working line.",
    ],
    industries: [
      "Aerospace & defense manufacturing",
      "Distribution & logistics",
      "Entertainment & tourism",
      "Manufacturing & fabrication",
      "Warehousing",
    ],
    districts: [
      "I-20 / SH-161 logistics corridor",
      "Lone Star Park area",
      "Grand Prairie Municipal Airport",
      "EpicCentral entertainment district",
    ],
    scenarios: [
      {
        h: "Aerospace & manufacturing capacity",
        p: "Subcontractors and fabricators use equipment financing and SBA loans to take on larger aerospace and defense orders.",
      },
      {
        h: "Distribution receivables",
        p: "Warehouses and distributors use invoice factoring and A/R financing to free up cash tied in net-30 and net-60 terms.",
      },
      {
        h: "Event & tourism staffing",
        p: "Vendors serving Lone Star Park and EpicCentral use short-term and same-day capital to scale for peak seasons.",
      },
    ],
    proximityNote:
      "Grand Prairie is a quick run west on I-30 or I-20; nearly every deal is handled remotely.",
    nearby: ["irving", "mesquite"],
  },

  mesquite: {
    slug: "mesquite",
    city: "Mesquite",
    county: "Dallas County",
    economy: [
      "Known statewide as the home of the Mesquite Championship Rodeo, Mesquite is also the metro's eastern gateway: a retail and distribution town where US-80, I-635, and I-20 funnel goods in and out. Town East Mall and a deep base of distributors, manufacturers, and healthcare providers anchor the local economy.",
      "Retailers and distributors here live and die on timing. A Mesquite distributor restocking for a seasonal push, a Town East-area retailer, and an equipment-heavy contractor all need capital that arrives before the opportunity does, which is why short-term, same-day, and inventory-driven financing fit the market.",
    ],
    industries: [
      "Retail & consumer",
      "Distribution & logistics",
      "Manufacturing",
      "Healthcare & services",
      "Events & hospitality (rodeo)",
    ],
    districts: [
      "Town East Mall district",
      "US-80 / I-635 logistics corridor",
      "Mesquite Rodeo & arena area",
      "Historic downtown Mesquite",
    ],
    scenarios: [
      {
        h: "Seasonal inventory",
        p: "Mesquite retailers and distributors use short-term and same-day loans to stock up before peak selling seasons.",
      },
      {
        h: "Distribution cash flow",
        p: "Wholesalers along US-80 use invoice factoring to turn delivered orders into same-week cash.",
      },
      {
        h: "Equipment & contracts",
        p: "Local manufacturers and contractors finance equipment to take on bigger orders without draining working capital.",
      },
    ],
    proximityNote:
      "Mesquite is just east on I-635 or US-80; the whole process runs by phone and upload.",
    nearby: ["garland", "grand-prairie"],
  },

  carrollton: {
    slug: "carrollton",
    city: "Carrollton",
    county: "Dallas & Denton Counties",
    economy: [
      "Carrollton blends a deep manufacturing and distribution base with one of the metro's most active small-business communities, including a large, long-established Korean-American business district along Old Denton Road. Around its historic downtown square and the Trinity Mills corridor, family-owned shops, restaurants, and light manufacturers set the tone.",
      "Owner-operated businesses borrow on relationships and cash flow. A Carrollton restaurant, an import-export wholesaler, and a small fabricator often need flexible, fast capital (a line of credit, short-term loan, or factoring) more than a slow, collateral-heavy bank process.",
    ],
    industries: [
      "Manufacturing & light industrial",
      "Distribution & import/export",
      "Restaurants & retail",
      "Professional & family-owned services",
      "Construction trades",
    ],
    districts: [
      "Old Denton Road business district",
      "Downtown Carrollton Square (DART Green Line)",
      "Trinity Mills corridor",
      "President George Bush Turnpike corridor",
    ],
    scenarios: [
      {
        h: "Owner-operated restaurants & retail",
        p: "Family-run Carrollton businesses use lines of credit and short-term loans for inventory, equipment, and seasonal staffing.",
      },
      {
        h: "Import/export & wholesale",
        p: "Distributors use invoice factoring and A/R financing to bridge the gap between shipping goods and getting paid.",
      },
      {
        h: "Light manufacturing",
        p: "Small fabricators finance equipment and materials to fulfill larger purchase orders.",
      },
    ],
    proximityNote:
      "Carrollton is minutes from the office via I-35E or the Bush Turnpike; most files never require a visit.",
    nearby: ["irving", "plano"],
  },

  richardson: {
    slug: "richardson",
    city: "Richardson",
    county: "Dallas & Collin Counties",
    economy: [
      "Richardson is the heart of the Telecom Corridor along US-75, a decades-old cluster of telecom, technology, and insurance employers anchored by CityLine and the University of Texas at Dallas. It's the most technology- and back-office-weighted economy of the inner-ring suburbs.",
      "Tech firms, insurers, and the vendors around UT Dallas borrow against contracts, payroll cycles, and growth rather than hard assets. A Richardson software vendor, an IT services firm, and a campus-adjacent restaurant group each tend to need a line of credit or working capital far more than equipment debt.",
    ],
    industries: [
      "Technology & telecom",
      "Insurance & financial services",
      "Professional & IT services",
      "Higher education (UT Dallas)",
      "Restaurants & retail (CityLine)",
    ],
    districts: [
      "Telecom Corridor (US-75)",
      "CityLine",
      "UT Dallas area",
      "Downtown / Main Street Richardson",
    ],
    scenarios: [
      {
        h: "Tech & IT services payroll",
        p: "Software and IT firms use a line of credit to cover payroll and growth between milestone client payments.",
      },
      {
        h: "Contract-based working capital",
        p: "Telecom and services vendors fund staffing and equipment ahead of long-cycle contracts with working capital.",
      },
      {
        h: "Campus-area hospitality",
        p: "Restaurants and retailers near UT Dallas and CityLine use short-term capital for build-outs and seasonal swings.",
      },
    ],
    proximityNote:
      "Richardson is straight down US-75 from the office; nearly every file is handled remotely.",
    nearby: ["plano", "garland"],
  },

  denton: {
    slug: "denton",
    city: "Denton",
    county: "Denton County",
    economy: [
      "Denton is a university town with an industrial backbone. The University of North Texas and Texas Woman's University drive a young, culture-rich economy of music venues, restaurants, and services around the historic courthouse square, while heavy manufacturing anchors a serious production base on the city's edges.",
      "That gives Denton two borrowing worlds. A downtown music venue or restaurant borrows on seasonal, student-driven cash flow, while a manufacturer or supplier borrows against equipment and contracts, and both benefit from a lender who understands a market at the metro's northern edge.",
    ],
    industries: [
      "Higher education (UNT, TWU)",
      "Manufacturing (heavy trucks, food, supplies)",
      "Music, arts & hospitality",
      "Restaurants & student-driven retail",
      "Agriculture & ag-services (north county)",
    ],
    districts: [
      "Historic Downtown Square",
      "UNT & TWU campus areas",
      "Rayzor Ranch",
      "I-35 manufacturing corridor",
    ],
    scenarios: [
      {
        h: "Student-season cash flow",
        p: "Denton venues, bars, and restaurants use short-term and same-day capital to staff and stock for the school-year rush.",
      },
      {
        h: "Manufacturing equipment",
        p: "Truck, food, and supply manufacturers finance equipment and lines to fulfill larger production runs.",
      },
      {
        h: "Seasonal & ag-adjacent",
        p: "North-county and ag-services businesses use working capital and short-term loans to bridge seasonal swings.",
      },
    ],
    proximityNote:
      "Denton sits at the north end of I-35; we run the entire process by phone and secure upload.",
    nearby: ["frisco", "carrollton"],
  },
};

export const getSuburbProfile = (slug: string): SuburbProfile | undefined =>
  SUBURB_PROFILES[slug];
